/**
 * fund-categories.ts
 *
 * Canonical fund category definitions and a mapper that normalises
 * SEBI / API category strings to our internal category keys.
 */

export const FUND_CATEGORIES = [
  {
    key: "large-cap",
    label: "Large Cap",
    description: "Funds investing primarily in the top 100 companies by market capitalisation.",
  },
  {
    key: "mid-cap",
    label: "Mid Cap",
    description: "Funds investing primarily in companies ranked 101-250 by market capitalisation.",
  },
  {
    key: "small-cap",
    label: "Small Cap",
    description: "Funds investing primarily in companies ranked beyond 250 by market capitalisation.",
  },
  {
    key: "flexi-cap",
    label: "Flexi Cap",
    description: "Funds that can invest across market capitalisations without any fixed allocation.",
  },
  {
    key: "elss-tax-saver",
    label: "ELSS / Tax Saver",
    description: "Equity-linked savings schemes offering tax deductions under Section 80C with a 3-year lock-in.",
  },
  {
    key: "index-funds",
    label: "Index Funds",
    description: "Passively managed funds that replicate a market index such as Nifty 50 or S&P 500.",
  },
  {
    key: "debt-liquid",
    label: "Debt & Liquid",
    description: "Funds investing in fixed-income instruments like government bonds, corporate debt, and money-market securities.",
  },
  {
    key: "hybrid",
    label: "Hybrid",
    description: "Funds investing in a mix of equity and debt to balance growth and stability.",
  },
  {
    key: "sectoral-thematic",
    label: "Sectoral / Thematic",
    description: "Funds concentrating on a specific sector (e.g. IT, pharma) or investment theme.",
  },
  {
    key: "multi-cap-value",
    label: "Multi Cap & Value",
    description: "Funds investing across market caps with a value-oriented or multi-cap strategy.",
  },
  {
    key: "focused",
    label: "Focused",
    description: "Funds holding a concentrated portfolio of up to 30 stocks across market capitalisations.",
  },
] as const;

export type FundCategoryKey = (typeof FUND_CATEGORIES)[number]["key"];

/**
 * Maps a SEBI / API category string to one of our internal category keys.
 * Falls back to the closest match; returns `null` if no match is found.
 */
export function fundCategoryFromApi(apiCategory: string): FundCategoryKey | null {
  const cat = apiCategory.toLowerCase().trim();

  // Large cap
  if (/\blarge\s*cap\b/.test(cat) && !/mid|small|multi|flexi/.test(cat)) return "large-cap";

  // Mid cap
  if (/\bmid\s*cap\b/.test(cat) && !/large|small|multi|flexi/.test(cat)) return "mid-cap";

  // Small cap
  if (/\bsmall\s*cap\b/.test(cat) && !/large|mid|multi|flexi/.test(cat)) return "small-cap";

  // Flexi cap
  if (/\bflexi\s*cap\b/.test(cat)) return "flexi-cap";

  // ELSS / Tax
  if (/\belss\b/.test(cat) || /\btax\s*sav/.test(cat)) return "elss-tax-saver";

  // Index / ETF
  if (/\bindex\b/.test(cat) || /\betf\b/.test(cat) || /\bpassive\b/.test(cat)) return "index-funds";

  // Debt / Liquid / Money market / Overnight / Gilt
  if (
    /\bdebt\b/.test(cat) ||
    /\bliquid\b/.test(cat) ||
    /\bmoney\s*market\b/.test(cat) ||
    /\bovernight\b/.test(cat) ||
    /\bgilt\b/.test(cat) ||
    /\bfloating\s*rate\b/.test(cat) ||
    /\bcorporate\s*bond\b/.test(cat) ||
    /\bbanking\s*(and|&)\s*psu\b/.test(cat) ||
    /\bshort\s*(duration|term)\b/.test(cat) ||
    /\bmedium\s*(duration|term)\b/.test(cat) ||
    /\blow\s*duration\b/.test(cat) ||
    /\bultra\s*short\b/.test(cat) ||
    /\blong\s*duration\b/.test(cat) ||
    /\bdynamic\s*bond\b/.test(cat) ||
    /\bcredit\s*risk\b/.test(cat)
  ) return "debt-liquid";

  // Hybrid
  if (
    /\bhybrid\b/.test(cat) ||
    /\bbalanced\b/.test(cat) ||
    /\baggressive\b/.test(cat) ||
    /\bconservative\b/.test(cat) ||
    /\barbitrage\b/.test(cat) ||
    /\bequity\s*savings\b/.test(cat) ||
    /\bmulti\s*asset\b/.test(cat)
  ) return "hybrid";

  // Sectoral / Thematic
  if (/\bsector/.test(cat) || /\bthematic\b/.test(cat)) return "sectoral-thematic";

  // Multi cap / Value / Contra / Dividend yield
  if (
    /\bmulti\s*cap\b/.test(cat) ||
    /\bvalue\b/.test(cat) ||
    /\bcontra\b/.test(cat) ||
    /\bdividend\s*yield\b/.test(cat)
  ) return "multi-cap-value";

  // Focused
  if (/\bfocused\b/.test(cat) || /\bfocus\b/.test(cat)) return "focused";

  return null;
}
